// src/components/layout/Sidebar.jsx
import { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../ui/LanguageToggle';
import ThemeToggle from '../ui/ThemeToggle';
import { getDocsUrl } from '../../utils/docsLink';
import { 
  HiOutlineChartPie, 
  HiOutlineBeaker, 
  HiOutlineLink,
  HiOutlineChip,
  HiOutlineLogout,
  HiOutlineLogin,
  HiOutlineCog,
  HiX, 
  HiOutlineQuestionMarkCircle,
  HiOutlineScale,
  HiOutlineUserGroup,
  HiArrowSmUp,
  HiArrowSmDown
} from 'react-icons/hi';

import logo from '../../assets/logo_logo.svg'; 

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const docsUrl = getDocsUrl(i18n.language);
  const usdtCtaUrl = 'https://t.me/m/y3J2Z6KRZDAy';
  const [isExchangeActionsOpen, setExchangeActionsOpen] = useState(false);
  const isAccountsRoute = location.pathname === '/accounts';

  const baseLinkClasses = "flex items-center px-4 py-3 text-gray-300 rounded-lg transition-colors duration-200";
  const activeLinkClasses = "bg-blue-600 text-white font-semibold";
  const inactiveLinkHoverClasses = "hover:bg-slate-700 hover:text-white";

  const isAdmin = user?.role?.toLowerCase() === 'admin';

  useEffect(() => {
    setExchangeActionsOpen(isAccountsRoute);
  }, [isAccountsRoute]);

  return (
    <aside 
      className={`fixed inset-y-0 left-0 z-30 w-64 bg-slate-900 border-r border-slate-700/50 p-4 flex flex-col
                 transform transition-transform duration-300 ease-in-out
                 ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                 lg:translate-x-0 lg:static lg:inset-0`}
    >
      <div className="flex items-center justify-between h-16 px-2 mb-2">
        
        {/* ИЗМЕНЕНИЯ ЗДЕСЬ: Адаптивные размеры для логотипа и текста */}
        <Link to="/" className="flex items-center gap-2 lg:gap-3 pt-1 hover:opacity-80 transition-opacity">
            <img 
              src={logo} 
              alt="Hedger Logo" 
              className="w-10 h-10 lg:w-14 lg:h-14 object-contain" 
            />
            <span className="text-white text-2xl lg:text-3xl font-bold leading-none mb-1 lg:mb-2">
              Hedger
            </span>
        </Link>
        
        <button onClick={onClose} className="lg:hidden p-1 text-gray-400 hover:text-white"><HiX className="w-6 h-6" /></button>
      </div>

      <nav className="flex-1 space-y-2 overflow-y-auto custom-scrollbar" onClick={onClose}>
        <NavLink to="/dashboard" className={({ isActive }) => `${baseLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkHoverClasses}`}>
          <HiOutlineChartPie className="h-6 w-6 mr-3" />
          <span>{t('sidebar.dashboard')}</span>
        </NavLink>

        <NavLink to="/pair-analysis" className={({ isActive }) => `${baseLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkHoverClasses}`}>
          <HiOutlineScale className="h-6 w-6 mr-3" />
          <span>{t('sidebar.pair_analysis') || 'Pair Analysis'}</span>
        </NavLink> 

        
        <NavLink to="/strategies" className={({ isActive }) => `${baseLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkHoverClasses}`}>
          <HiOutlineBeaker className="h-6 w-6 mr-3" />
          <span>{t('sidebar.my_strategies')}</span>
        </NavLink>
        <div>
          <NavLink
            to="/accounts"
            onClick={(event) => {
              event.stopPropagation();
              setExchangeActionsOpen(true);
            }}
            className={({ isActive }) => `${baseLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkHoverClasses}`}
          >
            <HiOutlineLink className="h-6 w-6 mr-3" />
            <span className="flex-1 whitespace-nowrap">{t('sidebar.connected_exchange')}</span>
          </NavLink>

          <div
            className={`overflow-hidden transition-all duration-300 ease-out ${
              isExchangeActionsOpen ? 'max-h-28 opacity-100 mt-2' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="mx-2 grid grid-cols-2 gap-1.5 rounded-xl border border-slate-700/60 bg-slate-800/55 p-2">
              <a
                href={usdtCtaUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
                className="inline-flex items-center justify-center gap-1 rounded-lg border border-blue-400/30 bg-blue-500/15 px-1.5 py-1.5 text-[10px] font-semibold text-blue-100 transition-colors hover:bg-blue-500/25 hover:text-white"
              >
                <HiArrowSmUp className="h-3.5 w-3.5" />
                <span>{t('sidebar.buy_usdt')}</span>
              </a>
              <a
                href={usdtCtaUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
                className="inline-flex items-center justify-center gap-1 rounded-lg border border-cyan-400/30 bg-cyan-500/15 px-1.5 py-1.5 text-[10px] font-semibold text-cyan-100 transition-colors hover:bg-cyan-500/25 hover:text-white"
              >
                <HiArrowSmDown className="h-3.5 w-3.5" />
                <span>{t('sidebar.sell_usdt')}</span>
              </a>
            </div>
          </div>

        </div>
        <NavLink to="/auto-trading" className={({ isActive }) => `${baseLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkHoverClasses}`}>
          <HiOutlineChip className="h-6 w-6 mr-3" />
          <span>{t('sidebar.auto_trading')}</span>
        </NavLink>

        {isAdmin && (
          <NavLink to="/admincontrol" className={({ isActive }) => `${baseLinkClasses} ${isActive ? 'bg-amber-600 text-white font-semibold' : 'text-amber-400 hover:bg-amber-900/20'}`}>
            <HiOutlineCog className="h-6 w-6 mr-3" />
            <span>{t('sidebar.admin_control') || 'Admin Control'}</span>
          </NavLink>
        )}
      </nav>

      <div className="mt-auto mb-4 space-y-2">
        <div className="space-y-2">
          <a
            href={docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className={`${baseLinkClasses} ${inactiveLinkHoverClasses}`}
          >
            <HiOutlineQuestionMarkCircle className="h-6 w-6 mr-3" />
            <span>{t('sidebar.faq')}</span>
          </a>
        </div>
        <NavLink to="/partner-program" onClick={onClose} className={({ isActive }) => `${baseLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkHoverClasses}`}>
          <HiOutlineUserGroup className="h-6 w-6 mr-3" />
          <span>{t('sidebar.partner_program')}</span>
        </NavLink>
      </div>

      <div className="pt-4 border-t border-slate-700/50">
        {user ? (
          <div className="flex items-center space-x-3 px-2">
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-blue-400 font-bold text-lg shrink-0">{user.login?.charAt(0).toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user.login}</p>
              {isAdmin && <p className="text-[9px] text-amber-500 font-bold uppercase tracking-tighter">Administrator</p>}
            </div>
            <div className="flex items-center space-x-1">
              <Link to="/settings" className="p-2 text-gray-400 rounded-full hover:bg-slate-700 hover:text-white"><HiOutlineCog className="w-6 w-6" /></Link>
              <button onClick={logout} className="p-2 text-gray-400 rounded-full hover:bg-slate-700 hover:text-white"><HiOutlineLogout className="w-6 w-6" /></button>
            </div>
          </div>
        ) : (
          <Link to="/login" className={`${baseLinkClasses} ${inactiveLinkHoverClasses}`}>
            <HiOutlineLogin className="h-6 w-6 mr-3" />
            <span>{t('sidebar.sign_in')}</span>
          </Link>
        )}
      </div>

      <div className="mt-4 space-y-2">
        <ThemeToggle />
        <LanguageToggle />
      </div>
    </aside>
  );
};

export default Sidebar;
